import { useEffect, useState } from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import Autocomplete, { AutocompleteChangeReason, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { InputAdornment, CircularProgress, Typography } from '@mui/material';
import { CONFIG } from '~/config';
import { ExternalRestClient } from '~/modules/requests';
import { Airport, AirportPage, Place } from '~/types';
import { Box } from '@mui/system';
interface LocationSelectorProps {
  name: string;
  location: Place | null;
  label: string;
  placeholder: string;
  required?: boolean;
  errorHelperText?: Array<string>;
  onLocationChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Place | null | string,
    reason?: AutocompleteChangeReason,
  ) => void;
}

const THROTTLE_IN_MS = 500;
const QUERY_TRIGGER_LENGTH = 3;
const LOCATIONS_PAGE_SIZE = 10;
const DEFAULT_QUERY_PAGE = 1;

function LocationSelector({
  errorHelperText,
  label,
  location,
  name,
  onLocationChange,
  placeholder,
  required,
}: LocationSelectorProps) {
  const [locations, setLocations] = useState<Place[]>([]);
  const [query, setQuery] = useState<string | null>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let throttleTimer: NodeJS.Timeout | number | undefined;

    if (query && query.length >= QUERY_TRIGGER_LENGTH) {
      setLoading(true);
      throttleTimer = setTimeout(() => {
        ExternalRestClient.get<AirportPage>(
          `${CONFIG.env.apiBaseUrl}v1/airports?pageNumber=${DEFAULT_QUERY_PAGE}&pageSize=${LOCATIONS_PAGE_SIZE}&query=${query}`,
        )
          .then(response => {
            const places = response?.data?.data.filter(airport => airport.iata).map(mapAirportToPlace) ?? [];
            setLocations(places);
            setLoading(false);
          })
          .catch(() => {
            // TODO show error message
            setLocations([]);
            setLoading(false);
          });
      }, THROTTLE_IN_MS);
    }

    if (!query || query.length < QUERY_TRIGGER_LENGTH) {
      setLocations([]);
    }

    return () => {
      clearTimeout(throttleTimer);
    };
  }, [query]);

  return (
    <Autocomplete
      clearOnBlur
      componentsProps={{ popper: { style: { width: 'fit-content' } } }}
      freeSolo
      handleHomeEndKeys
      id={name}
      onChange={onLocationChange}
      options={locations}
      getOptionLabel={(option) => `${(option as Place).city} (${(option as Place).iata})`}
      filterOptions={(options, { inputValue }) => {
        const filter = createFilterOptions<Place>();
        return filter(options, {
          inputValue,
          getOptionLabel: (option) =>
            `${option.city} (${option.iata}) ${option.title ?? ''}`.toLowerCase(),
        });
      }}
      renderOption={(props, option) => (
        <li {...props}>
          <Box>
            <Typography variant='h6' marginBottom={0}>
              <small> {option.title}</small>
            </Typography>
            <Typography variant='body2' marginTop={0}>
              <small>{option.city}, {option.country}</small>
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          onChange={text => {
            setQuery(text.target.value);
          }}
          required={required}
          value={location}
          autoComplete='new-password'
          InputProps={{
            ...params.InputProps,
            autoComplete: 'new-password',
            sx: {
              backgroundColor: 'white',
            },
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errorHelperText && errorHelperText.length > 0}
          helperText={errorHelperText ? errorHelperText.join('\n') : null}
          label={label}
          placeholder={placeholder}
        />
      )}
      selectOnFocus
      value={location}
    />
  );
}

export default LocationSelector;

const mapAirportToPlace = (airport: Airport): Place => {
  return { city: airport.city, country: airport.country, title: airport.name, type: 'airport', iata: airport.iata || '' };
};
